import { useCallback, useEffect, useRef } from 'react';
import { useStore } from 'zustand';

import { useReferenceAssistantGateway } from './ReferenceAssistantGatewayProvider';
import { useCitationRequestForMessage } from './useCitationRequestForMessage';
import { putCitationRequest, refetchCitationRequests } from '../api/citationRequestCache';
import {
  type CitationRequest,
  CitationRequestCreateError,
  type CitationRequestStatus,
  isInFlightStatus,
} from '../model/citationRequest';
import {
  type AttachedStatementReference,
  resolveNeedsCitation,
  type StatementCitationStatus,
} from '../model/extractedStatements';
import type { ReferenceAssistantGateway } from '../ports/referenceAssistantGateway';
import { openCitationRequestReview } from '../state/citationRequestReviewStore';
import {
  getProvideReferenceDraftStore,
  selectFilledCount,
} from '../state/provideReferenceDraftStore';
import { getStatementTriageStore } from '../state/statementTriageStore';

import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useTransitionEffect } from '@/hooks/useTransitionEffect';
import {
  trackCitationRequestCreated,
  trackCitationRequestCreateFailed,
  trackCitationRequestReviewOpened,
  trackProvideReferencesDismissed,
  trackStatementCitationTriageDismissed,
  trackStatementCitationTriageOpened,
} from '@/util/analyticsEvents';
import { queryClient } from '@/util/queryClient';
import { Sentry } from '@/util/sentry';

interface UseStatementTriageSheetInput {
  conversationId: string;
  messageSid: string;
  statementCount: number;
  /** Persisted citation-statuses array from the statement references. Read at
   * open time only, to report the already-flagged count on the OPENED
   * analytics event. */
  statementCitationStatuses: readonly StatementCitationStatus[] | undefined;
  /** The message's currently-attached references. Used when the
   * wizard transitions `searching → review` for a freshly-created
   * citation request: callers don't go through the chat-bubble pill
   * (which normally seeds the review store via
   * `openCitationRequestReview`), so this hook seeds the new
   * request's review store with any of its proposed references that
   * match a previously-published attached ref. Omitting (or passing
   * an empty array) leaves the approval set empty — fine for fresh
   * searches with no prior publish on the message. */
  currentAttachedStatementReferences?: readonly AttachedStatementReference[];
}

/**
 * Floor on how long the wizard stays in the `searching` phase, measured
 * from when it entered searching. A completion signal that arrives sooner
 * than this (an optimistic-cache hit, or a very fast backend) is held to
 * this floor so `searching → review` doesn't flash by before the expert
 * registers that a search ran.
 */
const MIN_SEARCH_DWELL_MS = 1_500;

/**
 * Delay applied after the real completion signal before committing the
 * review transition, so the per-statement progress bars' snap-to-100 % is
 * actually visible even when the search ran long enough that the floor
 * above has already elapsed. This is what preserves the "bar at 100 %
 * before transition" invariant now that timing is signal-driven rather
 * than paced to a known duration.
 */
const SEARCH_COMPLETE_SETTLE_MS = 500;

/**
 * Max time the wizard waits in `searching` for a completion signal before
 * surfacing "taking longer than expected" copy. Defensive only: the
 * query's in-flight poll normally rescues a dropped realtime event well
 * inside this window, and the persistent "Notify me when ready"
 * affordance is always available as a non-hanging exit.
 */
const MAX_SEARCH_WAIT_MS = 180_000;

/**
 * Re-POST the create for an active request the expert just engaged with,
 * so the backend adopts it and stamps `requestedAt`, then merge the
 * stamped response into the conversation cache. Best-effort: a failure
 * is captured to Sentry and never surfaces in the wizard.
 */
async function stampRequestedAt(
  gateway: ReferenceAssistantGateway,
  activeCitationRequest: CitationRequest,
  conversationId: string,
  messageSid: string
): Promise<void> {
  try {
    const stamped = await gateway.createCitationRequest({
      conversationId,
      messageSid,
      statementIndices: [...activeCitationRequest.statementIndices],
    });

    putCitationRequest(queryClient, conversationId, stamped);
  } catch (stampError) {
    Sentry.captureException(stampError, {
      tags: { component: 'useStatementTriageSheet', source: 'citation-request-adopt-stamp' },
      extra: {
        errorMessage: stampError instanceof Error ? stampError.message : String(stampError),
        citationRequestId: activeCitationRequest.id,
        conversationId,
        messageSid,
      },
    });
  }
}

/**
 * Shared behavior for the reference-request wizard — OPENED analytics,
 * the auto-start search, the `searching → review` sync, and dismiss.
 * Used by the native route and the web Modal wrapper so the platform
 * components only own their respective overlay primitives.
 *
 * The statement picker has been removed: opening the wizard fires
 * `POST /v2/citation-requests` for **all** statements automatically
 * (see `handleAutoStart`). On success `setQueryData` synchronously
 * merges the new request into the `['citationRequests', conversationId]`
 * cache so the chat-bubble pill flips to its Searching variant on the
 * next render — without waiting for the `invalidateQueries` refetch;
 * the subsequent `invalidateQueries` reconciles any server-enriched
 * fields. A create for a message that already has an active request is
 * an adopt on the backend: it returns the existing request with
 * `requestedAt` stamped instead of starting a duplicate workflow (the
 * 409 one-in-flight constraint was dropped in the same API change).
 * Auto-start still gates on `pickActiveCitationRequest` plus the
 * wait-for-first-fetch guard so a fresh create only fires when no
 * request exists; the requestedAt-stamp effect below deliberately
 * re-POSTs in the adopt case.
 */
export function useStatementTriageSheet({
  conversationId,
  messageSid,
  statementCount,
  statementCitationStatuses,
  currentAttachedStatementReferences,
}: UseStatementTriageSheetInput) {
  const store = getStatementTriageStore(messageSid);

  const gateway = useReferenceAssistantGateway();

  const isOpen = useStore(store, (state) => state.isOpen);

  const phase = useStore(store, (state) => state.phase);

  const entryMode = useStore(store, (state) => state.entryMode);

  const submittedIndices = useStore(store, (state) => state.submittedIndices);

  const provideStatementIndices = useStore(store, (state) => state.provideStatementIndices);

  const isSaving = useStore(store, (state) => state.isSaving);

  const errorMessage = useStore(store, (state) => state.errorMessage);

  /**
   * Subscribe to the active citation request for this message so the
   * wizard can transition `searching → review` when the backend
   * reports `awaiting_review`. The same hook drives the chat-bubble
   * pill's workflow variant, so its query cache is shared — no extra
   * fetch is incurred here. When the status reaches `failed` or
   * `cancelled` the sheet closes and lets the chat-bubble pill surface
   * the follow-up state. `published` also closes the wizard as a
   * defensive fallback (the backend normally goes through
   * `awaiting_review` first), and the pill re-enters the review sheet
   * directly because `published` stays reviewable —
   * see `pickActiveCitationRequest`.
   */
  const { citationRequest: activeCitationRequest, isLoading: isCitationRequestLoading } =
    useCitationRequestForMessage({
      conversationId,
      messageSid,
    });

  const { track } = useChatAnalytics();

  // Mirror the latest persisted statuses into a ref so the OPENED
  // transition effect reads the freshest array without re-subscribing.
  // A realtime refetch of the statement references can refresh this prop
  // while the sheet is open (rarely).
  const statusesRef = useRef(statementCitationStatuses);

  statusesRef.current = statementCitationStatuses;

  /**
   * Mirror of the latest currentAttachedStatementReferences so the
   * sync effect's deferred `commitReviewTransition` (scheduled via
   * setTimeout for the floor/settle delay) seeds the new review store
   * with the freshest attached set rather than a stale snapshot from
   * sync-effect-fire time.
   */
  const currentAttachedRef = useRef(currentAttachedStatementReferences);

  currentAttachedRef.current = currentAttachedStatementReferences;

  /**
   * Timestamp at which the wizard's phase first became `searching`
   * for the current run. The sync effect measures elapsed searching
   * time against it to apply the `MIN_SEARCH_DWELL_MS` floor, and the
   * max-wait effect uses it to arm the `MAX_SEARCH_WAIT_MS` timeout.
   * Reset to `null` whenever the wizard leaves the `searching` phase so
   * the next entry captures a fresh timestamp.
   */
  const searchingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (phase === 'searching') {
      if (searchingStartedAtRef.current === null) {
        searchingStartedAtRef.current = Date.now();
      }
    } else {
      searchingStartedAtRef.current = null;
    }
  }, [phase]);

  /**
   * Max-wait safety net. While the wizard is in `searching`, arm a timer
   * for the remaining `MAX_SEARCH_WAIT_MS` budget; on expiry with no
   * completion signal, flip `searchTimedOut` so the searching phase can
   * explain the delay instead of showing a bar that never fills. Cleared
   * whenever the wizard leaves searching (the phase swap unmounts the
   * copy anyway) or the completion path fires first. Defined after the
   * `searchingStartedAtRef` effect so the timestamp is set before this
   * reads it on the same commit.
   */
  useEffect(() => {
    if (phase !== 'searching') {
      return;
    }

    const startedAt = searchingStartedAtRef.current ?? Date.now();

    const remaining = Math.max(0, MAX_SEARCH_WAIT_MS - (Date.now() - startedAt));

    const timeoutId = setTimeout(() => {
      store.getState().markSearchTimedOut();
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [phase, store]);

  // Snapshot the already-flagged count at open time for the OPENED
  // analytics baseline. Re-snapshot on each false→true transition.
  const checkedCountBeforeRef = useRef(0);

  // Fire OPENED exactly once per close→open transition.
  useTransitionEffect(isOpen, (previous, next) => {
    if (previous || !next) {
      return;
    }

    const checkedCountBefore = countChecked(statusesRef.current, statementCount);

    checkedCountBeforeRef.current = checkedCountBefore;

    trackStatementCitationTriageOpened(track, {
      conversationId,
      messageSid,
      statementCount,
      checkedCountBefore,
      // Read fresh from the store: the trigger sets `entryMode` before
      // `open()`, so by the false→true transition it is already correct,
      // but reading the live value avoids any closure-staleness across
      // re-opens within the same mount.
      entryMode: store.getState().entryMode,
      openSource: store.getState().openSource,
    });
  });

  /**
   * Create the citation request for an explicit statement-index set and
   * transition the wizard into `searching`. Shared by the manual "Find
   * references" button (`handleSave`) and the auto-start effect.
   *
   * Assumes `beginSave()` has already run and any prior request needing
   * a cancel has been handled by the caller. On success it merges the
   * new request into the conversation cache, fires the created
   * analytics, ends the save, and calls `enterSearching` with the
   * duration picked for this submit. On failure it fires the failed
   * analytics and ends the save with the error so the wizard surfaces it
   * inline. Returns `true` on success.
   */
  const createSearchAndEnterSearching = useCallback(
    async (statementIndices: readonly number[]): Promise<boolean> => {
      try {
        const citationRequest = await gateway.createCitationRequest({
          conversationId,
          messageSid,
          statementIndices: [...statementIndices],
        });

        putCitationRequest(queryClient, conversationId, citationRequest);

        void refetchCitationRequests(queryClient, conversationId);

        trackCitationRequestCreated(track, {
          citationRequestId: citationRequest.id,
          conversationId,
          messageSid,
          statementCount: statementIndices.length,
        });

        // Single atomic transition: `enterSearching` itself clears
        // `isSaving`/`errorMessage` and flips `phase`, so there's no
        // intermediate `set()` where the wizard is `selection` + not-saving
        // + no-error (which `isAutoStartPending` would briefly read as a
        // re-shown spinner). Do NOT reintroduce a separate `endSave()` here.
        store.getState().enterSearching(statementIndices, citationRequest.id);

        return true;
      } catch (workflowError) {
        let errorKind: 'persist_failed' | 'unknown' = 'unknown';

        if (workflowError instanceof CitationRequestCreateError) {
          errorKind = workflowError.kind;
        } else if (workflowError instanceof Error) {
          errorKind = 'persist_failed';
        }

        const message =
          workflowError instanceof Error
            ? workflowError.message
            : 'Unable to start the reference search. Your selection was saved — try again.';

        trackCitationRequestCreateFailed(track, {
          conversationId,
          messageSid,
          errorKind,
        });

        store.getState().endSave(new Error(message));

        return false;
      }
    },
    [conversationId, gateway, messageSid, store, track]
  );

  /**
   * Auto-start the search for **all** statements when the wizard opens
   * fresh, so the expert never sees a statement picker. Fires once per
   * open and only for a brand-new search: the selection (launch) phase
   * with no active citation request (the sync effect owns in-flight /
   * review / published). Waits for the first citation-request fetch so
   * it can't race an already-existing active request. On create failure
   * the wizard shows an error + retry (see `isAutoStartPending`) and
   * this does not retry automatically — `autoStartFiredRef` stays set
   * until the next open.
   */
  const autoStartFiredRef = useRef(false);

  const handleAutoStart = useCallback(async () => {
    if (statementCount === 0) {
      return;
    }

    const allIndices = Array.from({ length: statementCount }, (_, index) => index);

    store.getState().beginSave();

    await createSearchAndEnterSearching(allIndices);
  }, [statementCount, store, createSearchAndEnterSearching]);

  useEffect(() => {
    if (!isOpen) {
      autoStartFiredRef.current = false;

      return;
    }

    if (autoStartFiredRef.current || isCitationRequestLoading) {
      return;
    }

    const state = store.getState();

    if (
      state.phase !== 'selection' ||
      activeCitationRequest ||
      state.isSaving ||
      statementCount === 0
    ) {
      return;
    }

    autoStartFiredRef.current = true;

    void handleAutoStart();
  }, [
    isOpen,
    phase,
    activeCitationRequest,
    isCitationRequestLoading,
    statementCount,
    store,
    handleAutoStart,
  ]);

  /**
   * Stamp `requestedAt` on an adopted automatic search. Backend-initiated
   * (automatic) searches are created with `requestedAt: null` — the
   * expert hasn't asked for them. Opening this wizard IS the expert's
   * explicit engagement, so when it opens onto an active request that
   * hasn't been stamped yet, re-POST the create: the backend adopts the
   * existing request and returns it with `requestedAt` set, and the
   * response is merged into the conversation cache. No created analytics
   * fire here — nothing new was created.
   *
   * Best-effort bookkeeping: a failure is captured to Sentry and never
   * blocks the searching/review UI. Fires at most once per request per
   * open — the id ref survives the poll-refetch identity churn on
   * `activeCitationRequest` and resets on close, so a failed attempt can
   * retry on the next open. Provide entry runs no search and never
   * stamps.
   */
  const requestedStampFiredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      requestedStampFiredRef.current = null;

      return;
    }

    if (entryMode === 'provide' || !activeCitationRequest) {
      return;
    }

    if (
      activeCitationRequest.requestedAt !== null ||
      requestedStampFiredRef.current === activeCitationRequest.id
    ) {
      return;
    }

    requestedStampFiredRef.current = activeCitationRequest.id;

    void stampRequestedAt(gateway, activeCitationRequest, conversationId, messageSid);
  }, [isOpen, entryMode, activeCitationRequest, conversationId, gateway, messageSid]);

  /**
   * Reconcile the wizard step, submitted indices, and submitted
   * citation request id from the live citation request. Runs in every
   * phase (not just `searching`) so the wizard step is preserved
   * across close+reopen and across bubble re-mount when the user
   * leaves and returns to the conversation:
   *
   *   - In-flight (`queued` / `searching`) → wizard step = `searching`,
   *     snapshot synced.
   *   - `awaiting_review` / `published` → wizard step = `review`,
   *     snapshot synced. `published` maps to `review` (not terminal)
   *     because publish is a sync, not a one-shot commit — the expert
   *     can keep editing the approval set and re-publishing.
   *   - Terminal (`failed` / `cancelled`) → wizard reset, but **only**
   *     when the wizard was actively tracking the request that just
   *     terminated. Other state is left alone so an unrelated terminal
   *     request can't bulldoze a fresh selection.
   *   - No active request → leave state alone (user may be composing
   *     a fresh selection, or the query may still be loading).
   *
   * The wizard ignores updates for citation requests it didn't capture
   * at send time. Bubble re-mount after the registry cleanup resets
   * `submittedCitationRequestId` to `null`, so this gate doesn't
   * block the seed-from-server path on conversation re-entry.
   *
   * # Race note
   *
   * If a backend invalidation arrives at the same render tick as the
   * user pressing **Close** during searching, both `phase = review`
   * and `close()` could fire. `close()` flips `isOpen` to false but
   * preserves `phase`, so when the user re-opens via the pill the
   * sync effect's `review` value is still there — no flash of stale
   * state, the wizard re-enters at the right step.
   */
  useEffect(() => {
    if (!activeCitationRequest) {
      return;
    }

    // Provide entry is independent of citation requests — it runs no
    // search. Bypass every force-transition below so a pre-existing
    // active/terminal request on this message can't yank the expert out
    // of the provide phase.
    if (entryMode === 'provide') {
      return;
    }

    const state = store.getState();

    if (
      state.submittedCitationRequestId &&
      state.submittedCitationRequestId !== activeCitationRequest.id
    ) {
      return;
    }

    const target = pickWizardTarget(activeCitationRequest.status);

    if (target === 'terminal') {
      if (state.submittedCitationRequestId === activeCitationRequest.id) {
        state.reset();
      }

      return;
    }

    // Snapshot sync — idempotent on matching id, so a same-id update
    // doesn't churn re-renders. New id (fresh mount, or wizard never
    // tracked this request) reseats the snapshot from the live request.
    if (state.submittedCitationRequestId !== activeCitationRequest.id) {
      state.enterSearching(activeCitationRequest.statementIndices, activeCitationRequest.id);
    }

    if (target === 'review' && state.phase !== 'review') {
      const wasSearching = state.phase === 'searching';

      /**
       * Commit the searching → review transition and fire the
       * impression event. Hoisted into a closure so both the
       * immediate and dwell-delayed branches below share the same
       * implementation. Seeds the new request's review store with
       * any of its proposed references that match a previously-
       * published attached ref (key: `statementIndex::url`), so
       * carry-over references from a prior request are pre-approved
       * when the expert arrives at review — same convention the
       * chat-bubble pill uses via `openCitationRequestReview`.
       */
      const commitReviewTransition = () => {
        const latest = store.getState();

        if (latest.phase === 'review') {
          return;
        }

        openCitationRequestReview(activeCitationRequest, currentAttachedRef.current ?? []);

        latest.enterReview();

        if (wasSearching) {
          trackCitationRequestReviewOpened(track, {
            citationRequestId: activeCitationRequest.id,
            conversationId,
            messageSid: activeCitationRequest.messageSid,
            candidateCount: activeCitationRequest.proposedReferences.length,
          });
        }
      };

      /**
       * Signal-driven hand-off. When the wizard was actively searching,
       * snap the progress bars to 100 % immediately (`markSearchComplete`)
       * and defer the phase swap by just enough to keep the transition
       * from feeling abrupt: at least `SEARCH_COMPLETE_SETTLE_MS` so the
       * snap is visible, and no sooner than `MIN_SEARCH_DWELL_MS` from
       * search start so a near-instant completion doesn't flash. The
       * cleanup clears the pending commit if the request changes (e.g. a
       * terminal status arrives mid-settle), letting the effect re-run
       * and reset instead. When the wizard wasn't in `searching` (reopen
       * after the backend already finished) it lands in review at once.
       */
      if (wasSearching) {
        state.markSearchComplete();

        const startedAt = searchingStartedAtRef.current;

        const elapsed = startedAt !== null ? Date.now() - startedAt : Number.POSITIVE_INFINITY;

        const delayMs = Math.max(SEARCH_COMPLETE_SETTLE_MS, MIN_SEARCH_DWELL_MS - elapsed);

        const timeoutId = setTimeout(commitReviewTransition, delayMs);

        return () => clearTimeout(timeoutId);
      }

      commitReviewTransition();
    }
  }, [activeCitationRequest, store, track, conversationId, entryMode]);

  const handleDismiss = useCallback(() => {
    const state = store.getState();

    /**
     * The Cancel button, X button, and Escape key all gate their
     * dismiss paths on `!isSaving` — but the Gluestack `Modal` /
     * `Actionsheet` backdrop click is wired straight to the
     * overlay's `onClose` prop and has no knowledge of the in-flight
     * save. Without this guard a backdrop tap during an auto-start
     * create fires `STATEMENT_CITATION_TRIAGE_DISMISSED` and closes the
     * sheet while the request is still completing.
     */
    if (state.isSaving) {
      return;
    }

    // The provide flow is a distinct experience with its own funnel —
    // emit its dedicated dismissal so drop-off is measurable against
    // `PROVIDE_REFERENCES_OPENED`. (The publish-success path closes the
    // surface without routing through `handleDismiss`, so a completed
    // publish never double-counts here.) The search flow keeps the
    // `STATEMENT_CITATION_TRIAGE_DISMISSED` event.
    if (state.entryMode === 'provide') {
      trackProvideReferencesDismissed(track, {
        conversationId,
        messageSid,
        statementCount: state.provideStatementIndices.length,
        filledCount: selectFilledCount(getProvideReferenceDraftStore(messageSid).getState()),
      });

      state.close();

      return;
    }

    // The picker is gone, so there are never local pending changes.
    trackStatementCitationTriageDismissed(track, {
      conversationId,
      messageSid,
      pendingChangeCount: 0,
    });

    state.close();
  }, [conversationId, messageSid, store, track]);

  /**
   * True while the wizard is auto-starting a fresh search — shows a
   * brief "starting search" spinner across the pre-fire frame and the
   * in-flight create POST. Mirrors the auto-start effect's guard, plus
   * `!errorMessage` so a create failure shows the error + retry instead
   * of a stuck spinner.
   *
   * Deliberately NOT gated on `isSaving`: the spinner must show on the
   * pre-fire frame (the native route renders before its `open()` effect
   * runs `beginSave`), so `isSaving` is false at that point. The success
   * path stays flicker-free because `enterSearching` flips `phase` away
   * from `selection` in the same atomic `set()` that clears the save —
   * see the note in `createSearchAndEnterSearching`.
   *
   * Deliberately NOT gated on `isOpen` for the same pre-paint reason —
   * every consumer renders this only inside an already-open surface
   * (the native route, the web modal's `isOpen` gate), so a "true while
   * closed" value is never observed in the UI.
   */
  const isAutoStartPending =
    !isCitationRequestLoading &&
    phase === 'selection' &&
    !activeCitationRequest &&
    statementCount > 0 &&
    !errorMessage;

  return {
    isOpen,
    phase,
    entryMode,
    submittedIndices,
    provideStatementIndices,
    activeCitationRequest,
    isSaving,
    errorMessage,
    isAutoStartPending,
    handleAutoStart,
    handleDismiss,
  };
}

type WizardTarget = 'searching' | 'review' | 'terminal';

/**
 * Map a citation-request status to the wizard step it should occupy.
 *
 *   - `awaiting_review` and `published`: `'review'`. Both leave the
 *     request reviewable — the expert can keep refining the approval
 *     set and re-publishing — so the wizard stays parked on the review
 *     step rather than auto-resetting.
 *   - In-flight statuses (`queued` / `searching`): `'searching'`.
 *   - `failed` / `cancelled`: `'terminal'`. The workflow has ended
 *     without the expert's approval set reaching the message, so
 *     (when the wizard was tracking this request) the store resets.
 */
function pickWizardTarget(status: CitationRequestStatus): WizardTarget {
  if (status === 'awaiting_review' || status === 'published') {
    return 'review';
  }

  if (isInFlightStatus(status)) {
    return 'searching';
  }

  return 'terminal';
}

function countChecked(
  statuses: readonly StatementCitationStatus[] | undefined,
  statementCount: number
): number {
  if (!statuses) {
    return 0;
  }

  let count = 0;

  for (let index = 0; index < statementCount; index += 1) {
    if (resolveNeedsCitation(statuses, index)) {
      count += 1;
    }
  }

  return count;
}
